import { memo, useCallback } from 'react';

import {
  abbreviateId,
  getOrderIncrements,
  Icon,
  IconName,
  InlineFormattedNumber,
  MixpanelEvent,
  Popover,
  Text,
  useCurrenciesContext,
  useMarketAccountsContext,
  useMixpanel,
  usePopoverState,
} from '@talos/kyoko';

import type { OwnOrderResult } from '@src/hooks/useOwnOrders';
import { useMarketsContext, useSecuritiesContext } from '@src/providers';
import { useDisplaySettings, useTradingSettings } from '@src/providers/AppConfigProvider';
import { isEqual } from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import { getTradingOrderDetailsRoute } from '../../containers/Routes/routes';
import { useCurrentAppLayoutId } from '../../hooks/useAppLayoutId';
import { OwnOrderContent, OwnOrderID, OwnOrderIndicator, OwnOrderRow, OwnOrderWrapper, Side } from './styles';

export const OwnOrderRows = memo(({ ownOrders }: { ownOrders: OwnOrderResult[] }) => {
  const { marketsByName } = useMarketsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { currenciesBySymbol } = useCurrenciesContext();

  const navigate = useNavigate();
  const layoutId = useCurrentAppLayoutId();
  const { enableAdvancedOrderDetails } = useTradingSettings();

  const handleOnOwnOrderClick = useCallback(
    (order: OwnOrderResult) => {
      navigate(
        getTradingOrderDetailsRoute({
          orderID: order.orderID,
          type: 'order',
          tab: enableAdvancedOrderDetails ? 'market-orders' : 'details',
          layoutId,
        })
      );
    },
    [enableAdvancedOrderDetails, navigate, layoutId]
  );

  return (
    <OwnOrderContent data-testid="own-order-rows">
      {ownOrders.map((order, i) => {
        const market = marketsByName.get(order.market);
        const marketAccount = marketAccountsByName.get(order.marketAccount);
        const security = securitiesBySymbol.get(order.symbol);
        if (!market || !security) {
          return null;
        }
        const currency = currenciesBySymbol.get(order.currency ?? '');
        const marketLabel = marketAccount != null ? marketAccount.DisplayName : market.DisplayName;
        const orderIncrements = getOrderIncrements({ Currency: order.currency }, security, currency);
        return (
          <OwnOrderRow key={i}>
            <OwnOrderID onClick={() => handleOnOwnOrderClick(order)}>#{abbreviateId(order.orderID)}</OwnOrderID>
            <div>
              <Side side={order.side}>{(order.side || '').toUpperCase()}</Side>{' '}
              <InlineFormattedNumber
                number={order.leavesQty}
                increment={orderIncrements.quantityIncrement}
                currency={order.currency}
              />{' '}
              <Icon icon={IconName.AtSymbol} size="fontSizeSmall" />{' '}
              <InlineFormattedNumber
                number={order.price}
                increment={security.DefaultPriceIncrement}
                specification={security?.PriceDisplaySpec}
                currency={security.QuoteCurrency}
              />{' '}
              on <Text color="colorTextImportant">{marketLabel}</Text>
            </div>
          </OwnOrderRow>
        );
      })}
    </OwnOrderContent>
  );
});

export interface OwnOrdersProps {
  isBid?: boolean;
  isOffer?: boolean;
  ownOrders: OwnOrderResult[];
}

export const OwnOrders = memo(
  function OwnOrders({ isBid, isOffer, ownOrders, ...props }: OwnOrdersProps) {
    const mixpanel = useMixpanel();
    const { showAllInPrices } = useDisplaySettings();
    const onPopoverOpen = useCallback(() => {
      mixpanel.track(MixpanelEvent.ViewOpenMarketOrder);
    }, [mixpanel]);
    const popover = usePopoverState({
      trigger: 'hover',
      usePortal: true,
      isSmall: true,
      placement: 'top',
      delay: undefined,
      onOpen: onPopoverOpen,
    });
    if (!ownOrders.length || showAllInPrices) {
      return null;
    }

    return (
      <OwnOrderWrapper isBid={isBid} isOffer={isOffer} {...props} data-testid={`own-order-${isBid ? 'bid' : 'offer'}`}>
        <Popover {...popover}>
          <OwnOrderIndicator isBid={isBid} isOffer={isOffer} isOpen={popover.isOpen} hasMany={ownOrders.length > 1} />
          <OwnOrderRows ownOrders={ownOrders} />
        </Popover>
      </OwnOrderWrapper>
    );
  },
  (p, n) => isEqual(p, n)
);
